import { literals } from "@/constants/literals";
import { useMemo, useState } from "react";

import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import useGarageStore from "@/store/useGarbage.store";
import { useNavigate } from "react-router-dom";
import type { CapturedPhoto } from "@/types/types.d";

import Textarea from "@/components/textarea/Textarea";
import InputCheckbox from "@/components/input-checkbox/InputCheckbox";
import { CameraCapture } from "../../components/CameraCapture";
import Modal from "@/components/modal/Modal";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { BASE_URL, NACELLE_CONFIGURATION } from "@/constants/constants";
import OrderHeader from "../components/OrderHeader";
import NacelleFinfingsInputSearchSelect from "@/components/input-search-select/NacelleFinfingsInputSearchSelect";
import { useDevice } from "@/store/useDevice";

interface FormData {
  description: string;
  visibleForClient: boolean;
  photos: CapturedPhoto[];
}

const initial_status = {
  description: "",
  visibleForClient: true,
  photos: [],
  id: "",
};

const assignGroupId = () => {
  const now = new Date();
  return now.getTime();
};

const NewGroupPage = () => {
  const [formData, setFormData] = useState<FormData>(initial_status);
  const { garageData, addGarageGroupData } = useGarageStore();
  const [viewModal, setViewModal] = useState(false);
  const [groupId, setGroupId] = useState(assignGroupId());
  const navigate = useNavigate();
  const { isMobile } = useDevice();

  const isNacelleFindings = useMemo(
    () =>
      garageData?.step === NACELLE_CONFIGURATION.FINDINGS &&
      garageData.garage === NACELLE_CONFIGURATION.GARAGE_ID,
    [garageData],
  );

  const handleOnChange = (
    id: string,
    value: string | boolean | CapturedPhoto[],
  ) => {
    setFormData((prevState) => {
      const tmp: any = { ...prevState };
      tmp[id] = value;
      return tmp;
    });
  };

  const isValid = () => {
    let condition = formData.photos.length > 0;
    if (isNacelleFindings) {
      condition = condition && formData.description.length > 0;
    }
    return condition;
  };

  const handleOnSubmit = (
    e: React.BaseSyntheticEvent<
      Event,
      EventTarget & HTMLFormElement,
      EventTarget
    >,
  ) => {
    e.preventDefault();
    if (!isValid()) return;
    setViewModal(true);
  };

  const goToNextCapturePhase = () => {
    if (!isValid()) return;
    addGarageGroupData({ ...formData, id: groupId });
    navigate(`${BASE_URL}/capture/garage/resume`);
  };

  const cleanPhotosData = () => {
    if (!isValid()) return;
    addGarageGroupData({ ...formData, id: groupId });
    setGroupId(assignGroupId());
    setFormData(initial_status);
    setViewModal(false);
  };

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <OrderHeader />
      <form
        onSubmit={handleOnSubmit}
        className="w-full flex-1 flex flex-col justify-between"
      >
        <div className="w-full px-4 md:px-14 pt-4 md:flex-1 md:flex md:flex-col md:justify-between">
          <div className="w-full flex-1">
            <CameraCapture
              onChange={(photos) => handleOnChange("photos", photos)}
              maxPhotos={10}
              photos={formData?.photos || undefined}
            />
          </div>
          <div className="w-full my-4 space-y-4 md:flex md:flex-col-reverse md:gap-y-4">
            {isNacelleFindings ? (
              <div className="md:max-w-lg w-full">
                <NacelleFinfingsInputSearchSelect
                  required
                  label={literals.description}
                  id={"description"}
                  name="description"
                  value={formData.description}
                  size={"medium"}
                  onChange={(new_value) =>
                    handleOnChange("description", new_value)
                  }
                />
              </div>
            ) : (
              <Textarea
                label={literals.description}
                onChange={(new_value) =>
                  handleOnChange("description", new_value)
                }
                name="description"
                value={formData.description}
                deleteMessage={literals.cleanDescription}
                size="medium"
                rows={isMobile ? 5 : 1}
              />
            )}

            <InputCheckbox
              title={literals.visibleForClients}
              onChange={(new_value) =>
                handleOnChange("visibleForClient", new_value)
              }
              checked={formData.visibleForClient}
              id="visibleForClient"
            />
          </div>
        </div>
        <div className="py-4 flex flex-col pt-4 border-t border-t-ib-pt-color-gray-300 md:flex md:flex-row md:justify-center">
          <div className="px-4 md:capture-button">
            <PrimaryButton
              size="medium"
              label={literals.continue}
              colour="red"
              disabled={!isValid()}
              type="submit"
            />
          </div>
        </div>
      </form>
      <Modal
        title={literals.continue}
        opened={viewModal}
        onClose={() => setViewModal(false)}
      >
        <div className="w-full flex flex-col gap-y-4">
          <SecondaryButton
            colour="red"
            label={literals.uploadMorePhotos}
            onClick={cleanPhotosData}
          />
          <PrimaryButton
            colour="red"
            icon="checkUnderline"
            label={literals.seeAllPhotos}
            onClick={goToNextCapturePhase}
          />
        </div>
      </Modal>
    </div>
  );
};

export default NewGroupPage;
